import clsx from "clsx"
import { FC, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useStopwatch } from "react-timer-hook"
import { CardsManyImg, WoodPlateImg } from "@/assets"
import { showToast } from "@/helpers/toast"
import { useMatchSearch } from "@/hooks/useMatchSearch"
import { useModal } from "@/modals"
import { useAppDispatch } from "@/store"
import {
  useGetMatchAverageSearchTimeQuery,
  useMatchCancelMutation
} from "@/store/apis/matchAPI"
import { useGetPlayerProfileQuery } from "@/store/apis/playerAPI"
import { setFindMatchMode } from "@/store/slices/findMatch"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./FindMatchModal.module.scss"

const modalType = "findMatch"

export const FindMatchModal: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { modal } = useModal(modalType)
  const { isMatchFound, handleSearchMatch } = useMatchSearch()

  const {
    seconds,
    minutes,
    start: startTimer,
    pause: pauseTimer
  } = useStopwatch({ autoStart: false })

  const { data: player } = useGetPlayerProfileQuery()
  const { data: matchAverageSearchTime } = useGetMatchAverageSearchTimeQuery()

  const [cancelMatch, { isLoading: isCancelMatchLoading }] =
    useMatchCancelMutation()

  const closeModal = () => {
    pauseTimer()
    modal.close()
  }

  const handleClose = () => {
    dispatch(setFindMatchMode("label"))
    modal.close()
  }

  const handleDecline = () => {
    cancelMatch()
      .then((data) => {
        if (data?.data?.status === "CANCELLED") {
          closeModal()
        } else {
          showToast.error(t("errors.somethingWentWrong"))
        }
      })
      .catch(() => {
        showToast.error(t("errors.somethingWentWrong"))
      })
  }

  const estimatedTime = useMemo(() => {
    const hours = Math.floor(matchAverageSearchTime?.averageWaitSec / 3600)
    const minutes = Math.floor(
      (matchAverageSearchTime?.averageWaitSec % 3600) / 60
    )
    const seconds = Math.floor(matchAverageSearchTime?.averageWaitSec % 60)

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    }

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
  }, [matchAverageSearchTime])

  const didStartRef = useRef(false)

  useEffect(() => {
    startTimer()
  }, [])

  useEffect(() => {
    if (didStartRef.current || !player) return
    didStartRef.current = true
    if (!player.inQueue) handleSearchMatch()
  }, [player])

  return (
    <Modal.Center
      type={modalType}
      className={clsx(styles.FindMatchModal)}
      body={{ className: styles.FindMatchModal_body }}
      isCanClose={false}
    >
      <Modal.StoneFlexContainer
        content={{ className: styles.FindMatchModal_content }}
        outside={
          <>
            <div className={clsx(styles.FindMatchModal_timer)}>
              <img src={WoodPlateImg} alt="" />
              <OutlinedText className={clsx(styles.FindMatchModal_timer_text)}>
                {String(minutes).padStart(2, "0")} :{" "}
                {String(seconds).padStart(2, "0")}
              </OutlinedText>
            </div>
            <div className={clsx(styles.FindMatchModal_buttons)}>
              {!isMatchFound && (
                <>
                  <Button
                    className={clsx(styles.FindMatchModal_button)}
                    onClick={handleClose}
                    disabled={isCancelMatchLoading}
                  >
                    {t("button.close")}
                  </Button>
                  <Button
                    className={clsx(styles.FindMatchModal_button)}
                    styles="red"
                    onClick={handleDecline}
                    isLoading={isCancelMatchLoading}
                  >
                    {t("button.decline")}
                  </Button>
                </>
              )}
            </div>
          </>
        }
      >
        {isMatchFound ? (
          <>
            <OutlinedText className={clsx(styles.FindMatchModal_title)}>
              {t("findMatchModal.opponentFound")}
            </OutlinedText>
            <div className={clsx(styles.FindMatchModal_text, styles.subtitle)}>
              {t("findMatchModal.roomCreating")}
            </div>
            <div className={clsx(styles.FindMatchModal_text, styles.text)}>
              <span>{t("findMatch.text")}</span>
            </div>
          </>
        ) : (
          <>
            <OutlinedText className={clsx(styles.FindMatchModal_title)}>
              {t("findMatch.title")}
            </OutlinedText>
            <div className={clsx(styles.FindMatchModal_text, styles.subtitle)}>
              {t("findMatchModal.estimatedTime")}: {estimatedTime}
            </div>
            <div className={clsx(styles.FindMatchModal_text, styles.text)}>
              <span>{t("findMatch.text")}</span>
            </div>
          </>
        )}

        <div className={clsx(styles.FindMatchModal_logo)}>
          <img src={CardsManyImg} alt="" />
        </div>
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}
